/**
 * main.tsx — SPA entry point
 *
 * Auth gate (AUTH-01 / Plan 07):
 *   AuthProvider   — hydrates Cognito session from localStorage on mount (AUTH-02)
 *   RequireAuth    — blocks the app behind a valid session; shows LoginForm if no user
 *   AuthedApp      — T-649: the authed view switch (map ↔ admin page), router-less
 *
 * Order matters:
 *   QueryClientProvider must wrap AuthedApp (react-query hooks used in map layers).
 *   AuthProvider must be outside RequireAuth (provides the context RequireAuth consumes).
 */
import './index.css' // Tailwind base + :root tokens + .dark tokens
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './auth/AuthContext'
import { RequireAuth } from './auth/RequireAuth'
import { AppToaster } from './components/ui/toaster'
import { AuthedApp } from './pages/AuthedApp'
import { ErrorBoundary } from './components/ErrorBoundary'
import { initAnalytics } from './lib/analytics'
import { initTheme } from './lib/theme'

// T-126: initialise PostHog once at boot. No-ops (zero network) when VITE_POSTHOG_KEY
// is absent — the kill-switch — so this is safe in every environment.
initAnalytics()

// T-156: apply the resolved theme and attach the live prefers-color-scheme listener
// (only acts in 'system' mode). The no-flash class was already set by the inline script
// in index.html; this keeps it in sync app-wide, including the login screen.
initTheme()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30-second default freshness window
      retry: 1,
    },
  },
})

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element #root not found in index.html')
}

createRoot(rootElement).render(
  <StrictMode>
    {/* BEAD-0037: root-level boundary — an unforeseen throw degrades to a recoverable
        fallback (Reload → login screen) instead of unmounting the root to a blank page. */}
    <ErrorBoundary>
      <AuthProvider>
        <RequireAuth>
          <QueryClientProvider client={queryClient}>
            <AuthedApp />
            {/* Sonner toast surface (T-083) — portal-mounted; renders app-wide toasts. */}
            <AppToaster />
          </QueryClientProvider>
        </RequireAuth>
      </AuthProvider>
    </ErrorBoundary>
  </StrictMode>,
)
